import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../utils/api';
import { FaBuilding, FaIndustry, FaMapMarkerAlt, FaGlobe, FaUsers, FaCalendar, FaFileAlt, FaPhone, FaEnvelope, FaCheckCircle, FaUpload } from 'react-icons/fa';

const CreateCompany = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingCompany, setCheckingCompany] = useState(true);
  const [customIndustry, setCustomIndustry] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    location: {
      city: '',
      state: '',
      country: 'India'
    },
    website: '',
    size: '',
    foundedYear: '',
    logo: null,
    specialties: '',
    // Company Documents
    aadharCard: null,
    panCard: null,
    gstCertificate: null,
    udyamAadhar: null,
    registeredEmail: '',
    registeredPhone: ''
  });

  useEffect(() => {
    checkExistingCompany();
  }, []);

  const checkExistingCompany = async () => {
    try {
      const { data } = await API.get('/companies/my-company');
      if (data) {
        toast.info('You already have a company profile');
        navigate('/profile');
      }
    } catch (error) {
      // No company found, can create new one
    } finally {
      setCheckingCompany(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Company description is required');
      return;
    }
    if (!formData.industry) {
      toast.error('Industry is required');
      return;
    }
    if (formData.industry === 'Other' && !customIndustry.trim()) {
      toast.error('Please specify your industry');
      return;
    }
    if (!formData.location.city.trim()) {
      toast.error('City is required');
      return;
    }

    // Validate required documents
    if (!formData.aadharCard) {
      toast.error('Aadhar card of owner is required');
      return;
    }
    if (!formData.panCard) {
      toast.error('PAN card of company is required');
      return;
    }
    if (!formData.gstCertificate) {
      toast.error('GST certificate is required');
      return;
    }
    if (!formData.udyamAadhar) {
      toast.error('Registration certificate (Udyam Aadhar) is required');
      return;
    }
    if (!formData.registeredEmail.trim()) {
      toast.error('Registered email of company is required');
      return;
    }
    if (!formData.registeredPhone.trim()) {
      toast.error('Registered contact number of company is required');
      return;
    }

    setLoading(true);

    try {
      // Convert specialties to array
      const specialtiesArray = formData.specialties
        ? formData.specialties.split(',').map(s => s.trim()).filter(s => s)
        : [];

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('industry', formData.industry === 'Other' ? customIndustry : formData.industry);
      formDataToSend.append('location[city]', formData.location.city);
      formDataToSend.append('location[state]', formData.location.state);
      formDataToSend.append('location[country]', formData.location.country);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('size', formData.size);
      formDataToSend.append('foundedYear', formData.foundedYear ? parseInt(formData.foundedYear) : '');

      // Append logo file if selected
      if (formData.logo && formData.logo instanceof File) {
        formDataToSend.append('logo', formData.logo);
      }

      formDataToSend.append('specialties', JSON.stringify(specialtiesArray));

      // Append company documents
      if (formData.aadharCard instanceof File) {
        formDataToSend.append('aadharCard', formData.aadharCard);
      }
      if (formData.panCard instanceof File) {
        formDataToSend.append('panCard', formData.panCard);
      }
      if (formData.gstCertificate instanceof File) {
        formDataToSend.append('gstCertificate', formData.gstCertificate);
      }
      if (formData.udyamAadhar instanceof File) {
        formDataToSend.append('udyamAadhar', formData.udyamAadhar);
      }
      formDataToSend.append('registeredEmail', formData.registeredEmail);
      formDataToSend.append('registeredPhone', formData.registeredPhone);

      const { data } = await API.post('/companies', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Company profile created! Now let\'s set up your subscription to post jobs.');
      // Store company data in sessionStorage for immediate use
      if (data.company) {
        sessionStorage.setItem('newCompanyData', JSON.stringify(data.company));
      }
      setTimeout(() => {
        navigate('/plans', { state: { companyCreated: true, companyData: data.company } });
      }, 2000);
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error.response?.data?.message || 'Failed to create company profile');
    } finally {
      setLoading(false);
    }
  };

  if (checkingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex-grow w-full h-full flex bg-gray-50 font-sans">
      
      {/* Left Panel - Hero/Illustration (Hidden on mobile) */}
      <div 
        className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12 overflow-hidden shadow-2xl z-10"
        style={{ background: 'linear-gradient(135deg, #00c2ab 0%, rgba(0, 194, 171, 0.7) 100%)' }}
      >
        {/* Abstract Background Shapes (Glassmorphism) */}
        <div className="absolute top-[-15%] left-[-15%] w-96 h-96 bg-white/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-80 h-80 bg-white/20 rounded-full blur-3xl"></div>
        
        {/* Content */}
        <div className="relative z-10 mt-10">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white font-medium mb-6">
            ✨ Employer Portal
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Build Your <br/> Employer Brand.
          </h1>
          <p className="text-white/90 text-lg mb-10 leading-relaxed max-w-md">
            Set up your company profile to start posting jobs, managing applications, and hiring top talent seamlessly.
          </p>
          
          {/* Decorative Feature List */}
          <div className="space-y-4">
             <div className="flex items-center gap-4 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/20 shadow-lg transform hover:-translate-y-1 transition-transform duration-300">
                <div className="p-3 bg-white/20 rounded-xl">
                  <FaBuilding className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Verify Your Business</h3>
                  <p className="text-white/70 text-sm">Upload documents to get a verified badge.</p>
                </div>
             </div>
             
             <div className="flex items-center gap-4 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/20 shadow-lg transform hover:-translate-y-1 transition-transform duration-300">
                <div className="p-3 bg-white/20 rounded-xl">
                  <FaUsers className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Hire Top Talent</h3>
                  <p className="text-white/70 text-sm">Post jobs and reach thousands of candidates.</p>
                </div>
             </div>
          </div>
        </div>
        
        {/* Illustration Art */}
        <div className="relative z-10 mt-auto pt-12">
          <div className="w-full h-48 bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 shadow-2xl flex items-center justify-center relative overflow-hidden">
             <div className="absolute inset-0 bg-gradient-to-t from-white/10 to-transparent"></div>
             <FaIndustry className="text-white/30 text-8xl transform -rotate-12" />
             <div className="absolute top-4 right-6 w-16 h-16 bg-white/10 rounded-full blur-xl"></div>
             <div className="absolute bottom-4 left-6 w-12 h-12 bg-white/20 rounded-full blur-lg"></div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form (Scrollable) */}
      <div className="w-full lg:w-7/12 flex flex-col h-full overflow-y-auto bg-gray-50 custom-scrollbar">
         <div className="p-6 sm:p-10 lg:p-16 max-w-4xl mx-auto w-full">
            
            {/* Mobile Header (Hidden on Desktop) */}
            <div 
              className="lg:hidden mb-8 p-8 rounded-3xl text-white shadow-lg relative overflow-hidden" 
              style={{ background: 'linear-gradient(135deg, #00c2ab 0%, rgba(0, 194, 171, 0.7) 100%)' }}
            >
               <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/20 rounded-full blur-2xl"></div>
               <h1 className="text-3xl font-bold mb-2 relative z-10">Create Company</h1>
               <p className="text-white/90 text-sm relative z-10">Set up your profile to start hiring.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* --- Section 1: Basic Information --- */}
              <div className="bg-white p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,194,171,0.06)] border border-primary/10 transition-shadow hover:shadow-[0_8px_30px_rgb(0,194,171,0.12)] duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <FaBuilding />
                  </div>
                  Basic Details
                </h2>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="e.g., Tech Innovations Pvt Ltd"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Industry *</label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300 appearance-none"
                        required
                      >
                        <option value="">Select Industry</option>
                        <option value="IT & Software">IT & Software</option>
                        <option value="Finance & Banking">Finance & Banking</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="E-commerce">E-commerce</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Retail">Retail</option>
                        <option value="Telecommunications">Telecommunications</option>
                        <option value="Media & Entertainment">Media & Entertainment</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {formData.industry === 'Other' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Specify Industry *</label>
                        <input
                          type="text"
                          value={customIndustry}
                          onChange={(e) => setCustomIndustry(e.target.value)}
                          className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                          placeholder="e.g., Agriculture"
                          required
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company Size</label>
                      <select
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300 appearance-none"
                      >
                        <option value="">Select Size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1000 employees</option>
                        <option value="1001-5000">1001-5000 employees</option>
                        <option value="5000+">5000+ employees</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300 resize-none"
                      placeholder="Describe your company's mission and values..."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Specialties</label>
                    <input
                      type="text"
                      name="specialties"
                      value={formData.specialties}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="e.g., Web Development, AI, Cloud Solutions (comma separated)"
                    />
                  </div>
                  
                  <div className="pt-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Logo</label>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden">
                        {formData.logo && formData.logo instanceof File ? (
                          <img src={URL.createObjectURL(formData.logo)} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <FaBuilding className="text-primary/50 text-2xl" />
                        )}
                      </div>
                      <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm">
                        <input
                          type="file"
                          name="logo"
                          onChange={handleChange}
                          accept="image/*"
                          className="hidden"
                        />
                        Upload Logo
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Section 2: Location & Web --- */}
              <div className="bg-white p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,194,171,0.06)] border border-primary/10 transition-shadow hover:shadow-[0_8px_30px_rgb(0,194,171,0.12)] duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <FaGlobe />
                  </div>
                  Location & Web
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Founded Year</label>
                    <input
                      type="number"
                      name="foundedYear"
                      value={formData.foundedYear}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="e.g., 2015"
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      name="location.city"
                      value={formData.location.city}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="e.g., Mumbai"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      name="location.state"
                      value={formData.location.state}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="e.g., Maharashtra"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                    <input
                      type="text"
                      name="location.country"
                      value={formData.location.country}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="India"
                    />
                  </div>
                </div>
              </div>

              {/* --- Section 3: Contact Info --- */}
              <div className="bg-white p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,194,171,0.06)] border border-primary/10 transition-shadow hover:shadow-[0_8px_30px_rgb(0,194,171,0.12)] duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <FaPhone />
                  </div>
                  Contact Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Registered Email *</label>
                    <input
                      type="email"
                      name="registeredEmail"
                      value={formData.registeredEmail}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="info@company.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Registered Phone *</label>
                    <input
                      type="tel"
                      name="registeredPhone"
                      value={formData.registeredPhone}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary focus:bg-white hover:bg-white transition-all duration-300"
                      placeholder="+91 1234567890"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* --- Section 4: Required Documents --- */}
              <div className="bg-gradient-to-br from-primary/5 to-white p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,194,171,0.06)] border border-primary/20">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg text-primary">
                        <FaFileAlt />
                      </div>
                      Verification Documents
                    </h2>
                    <p className="text-sm text-gray-600">Please upload clear, valid documents for admin verification.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Aadhar */}
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 hover:border-primary/50 transition-colors">
                    <label className="block text-sm font-bold text-gray-800 mb-3">Owner's Aadhar Card *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed ${formData.aadharCard ? 'border-primary/50 bg-primary/5' : 'border-gray-300 hover:bg-gray-50'} rounded-xl cursor-pointer transition-colors`}>
                      <input type="file" name="aadharCard" onChange={handleChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                      {formData.aadharCard ? (
                        <div className="flex items-center gap-2 text-primary font-medium">
                          <FaCheckCircle /> <span className="truncate max-w-[150px]">{formData.aadharCard.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-gray-500">
                          <FaUpload className="mb-2" />
                          <span className="text-xs">Click to upload (PDF/Image)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* PAN */}
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 hover:border-primary/50 transition-colors">
                    <label className="block text-sm font-bold text-gray-800 mb-3">Company PAN Card *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed ${formData.panCard ? 'border-primary/50 bg-primary/5' : 'border-gray-300 hover:bg-gray-50'} rounded-xl cursor-pointer transition-colors`}>
                      <input type="file" name="panCard" onChange={handleChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                      {formData.panCard ? (
                        <div className="flex items-center gap-2 text-primary font-medium">
                          <FaCheckCircle /> <span className="truncate max-w-[150px]">{formData.panCard.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-gray-500">
                          <FaUpload className="mb-2" />
                          <span className="text-xs">Click to upload (PDF/Image)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* GST */}
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 hover:border-primary/50 transition-colors">
                    <label className="block text-sm font-bold text-gray-800 mb-3">GST Certificate *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed ${formData.gstCertificate ? 'border-primary/50 bg-primary/5' : 'border-gray-300 hover:bg-gray-50'} rounded-xl cursor-pointer transition-colors`}>
                      <input type="file" name="gstCertificate" onChange={handleChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                      {formData.gstCertificate ? (
                        <div className="flex items-center gap-2 text-primary font-medium">
                          <FaCheckCircle /> <span className="truncate max-w-[150px]">{formData.gstCertificate.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-gray-500">
                          <FaUpload className="mb-2" />
                          <span className="text-xs">Click to upload (PDF/Image)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Udyam */}
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 hover:border-primary/50 transition-colors">
                    <label className="block text-sm font-bold text-gray-800 mb-3">Udyam Aadhar *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed ${formData.udyamAadhar ? 'border-primary/50 bg-primary/5' : 'border-gray-300 hover:bg-gray-50'} rounded-xl cursor-pointer transition-colors`}>
                      <input type="file" name="udyamAadhar" onChange={handleChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                      {formData.udyamAadhar ? (
                        <div className="flex items-center gap-2 text-primary font-medium">
                          <FaCheckCircle /> <span className="truncate max-w-[150px]">{formData.udyamAadhar.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-gray-500">
                          <FaUpload className="mb-2" />
                          <span className="text-xs">Click to upload (PDF/Image)</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* --- Submit Actions --- */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 pb-12">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] py-4 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgb(0,194,171,0.4)] hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex justify-center items-center"
                >
                  {loading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Profile...
                    </div>
                  ) : 'Complete Company Profile'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 py-4 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:border-primary hover:text-primary transition-colors text-lg"
                >
                  Cancel
                </button>
              </div>

            </form>
         </div>
      </div>
    </div>
  );
};

export default CreateCompany;
